import React, { useRef, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import chatPng from "../../assets/chatPng.svg";
import fusoChat from "../../assets/fusoChat.svg";
import micIcon from "../../assets/mic-icon.svg";
import micActiveIcon from "../../assets/mic-active.svg";
import logo from "../../assets/fuso-transparent.svg";
import sendGray from "../../assets/sendGray.svg";
import sendWhite from "../../assets/sendWhitePnc.svg";
import UserMessage from "../UserMessage/UserMessage";
import FusoChatMessage from "../FusoChatMessage/FusoChatMessage";
import FusoChatEndMessage from "../FusoChatEndMessage/FusoChatEndMessage";
import YesSelection from "../YesSelection/YesSelection";
import RephraseQuestion from "../RephraseQuestion/RephraseQuestion";
import SearchParameters from "../SearchParameters/SearchParameters";
import PromptSuggestion from "../PromptSuggestion/PromptSuggestion";
import "./Main.css";

// Main component with responsive Begin Chat button
const Main = ({
  isChatStarted,
  handleStartChat,
  messages,
  isThinking,
  dots,
  showSearchParams,
  searchType,
  setSearchType,
  vinInput,
  handleVinInputChange,
  isLoadingVin,
  vinError,
  modelInput,
  classificationInput,
  handleModelInputChange,
  handleClassificationInputChange,
  isLoadingModelClass,
  modelClassError,
  isSearchValid,
  groups,
  subGroups,
  selectedGroup,
  selectedGroupMainGroup,
  selectedSubGroup,
  selectedSubGroupMainGroup,
  groupDropdownOpen,
  subGroupDropdownOpen,
  setGroupDropdownOpen,
  setSubGroupDropdownOpen,
  handleGroupSelect,
  handleSubGroupSelect,
  isLoadingSubGroups,
  resetSearchState,
  conversationCycleId,
  handleYesSelectionOption,
  handleFeedback,
  handleResetQuery,
  askAnotherQueryClicked,
  userInput,
  handleUserInputChange,
  handleKeyPress,
  isSendButtonDisabled,
  handleSendMessage,
  isListening,
  toggleSpeechRecognition,
  showPromptSuggestion,
  selectedPromptIndex,
  promptSuggestions,
  handlePromptSuggestionSelect,
  dismissPromptSuggestion,
  onValidationChange,
  // ADD THIS PROP to pass the selected language from parent
  selectedLanguage,
}) => {
  const { t } = useTranslation();
  const chatContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const getMessageText = (message) => {
    if (message.textKey) {
      return t(message.textKey);
    }
    return message.text;
  };

  // Enhanced smooth scroll with customizable duration - SAME AS FILE 1
  const scrollToBottom = useCallback((duration = 600) => {
    try {
      if (!chatContainerRef.current) return;

      const container = chatContainerRef.current;
      const startScrollTop = container.scrollTop;
      const targetScrollTop = container.scrollHeight - container.clientHeight;
      const distance = targetScrollTop - startScrollTop;

      // Don't scroll if already at bottom or no distance to scroll
      if (Math.abs(distance) < 10) return;

      const startTime = performance.now();

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Smooth ease-out cubic for natural deceleration
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);

        container.scrollTop = startScrollTop + distance * easeOutCubic;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    } catch (error) {
      console.error("Error scrolling to bottom:", error);
    }
  }, []);

  // Fallback scroll using scrollIntoView for browsers that support it better - ADDED FROM FILE 1
  const scrollToBottomFallback = useCallback(() => {
    try {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "nearest",
        });
      }
    } catch (error) {
      console.error("Error with fallback scroll:", error);
    }
  }, []);

  // Debounced scroll to prevent too many calls - ENHANCED FROM FILE 1
  const debouncedScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      // Try custom smooth scroll first
      scrollToBottom(600); // 6 second smooth scroll

      // Fallback after a delay if needed
      setTimeout(() => {
        scrollToBottomFallback();
      }, 500);
    }, 100);
  }, [scrollToBottom, scrollToBottomFallback]);

  // Main scroll effect - SAME AS FILE 1
  useEffect(() => {
    debouncedScroll();

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, isThinking, debouncedScroll]);

  const formatResponse = (text) => {
    if (!text) return "";
    return text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>").replace(/\n/g, "<br/>");
  };

  return (
    <main
      className={
        isChatStarted
          ? "p-2 py-2 overflow-y-auto flex flex-col bg-[#FFFFFF] mainOpen"
          : "p-8 flex-1 mobileMain"
      }
    >
      {!isChatStarted ? (
        // Welcome Screen
        <div className=" mx-auto text-center">
          <div className="mb-[40px] flex flex-col items-center mobHeading">
            <h1 className="text-3xl font-bold pncHeader">
              {t("welcomeTitleShop")}
            </h1>
            <p className="text-gray-600 mb-8 pncSubHeader">
              {t("welcomeSubtitle")}
            </p>
            {/* Desktop Begin Chat button - this stays the same, centered */}
            <button
              onClick={handleStartChat}
              className="bg-custom text-white px-8 py-3 rounded font-medium hover:bg-custom/90 transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-custom focus:ring-opacity-50 newChatPnc hidden sm:flex"
            >
              <div className="pncBtn">
                <img src={chatPng} alt="Chat Icon" />
                <p className="chatPnc">{t("beginChat")} </p>
              </div>
            </button>
          </div>

          {/* Features Section */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 text-left max-w-4xl mx-auto cardsGapPnc">
            {[
              {
                img: "/images/clockPnc.svg",
                title: t("availability24x7"),
                desc: t("availability24x7Desc"),
              },
              {
                img: "/images/instantPng.svg",
                title: t("instantResponse"),
                desc: t("instantResponseDesc"),
              },
              {
                img: "/images/consistencyPnc.svg",
                title: t("consistency"),
                desc: t("consistencyDesc"),
              },
              {
                img: "/images/scalabilityPng.svg",
                title: t("scalability"),
                desc: t("scalabilityDesc"),
              },
              {
                img: "/images/recogPnc.svg",
                title: t("helpfulPrompts"),
                desc: t("helpfulPromptsDesc"),
              },
              {
                img: "/images/privacyPnc.svg",
                title: t("dataPrivacy"),
                desc: t("dataPrivacyDesc"),
              },
            ].map((item, index) => (
              <div
                key={`${conversationCycleId}-feature-${index}`}
                className="p-6 bg-white rounded-lg border border-gray-100 hover:shadow-md transition-shadow cradPnc"
              >
                <div className="text-custom mb-[16px] cardImagePnc">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="h-16 object-contain imgIcon"
                  />
                </div>
                <h3 className="font-semibold mb-[4px] titleCard">
                  {item.title}
                </h3>
                <p className="text-sm titleSubHeadCard">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Mobile Begin Chat button - fixed at the bottom */}
          <div className="fixed bottom-0 left-0 right-0 p-4 sm:hidden">
            <button
              onClick={handleStartChat}
              className="bg-black text-white w-full py-3 rounded-full font-medium flex items-center justify-center space-x-2"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 12H16"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 16V8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t("beginChat")}</span>
            </button>
          </div>
        </div>
      ) : (
        // Chat Interface
        <>
          <div
            className={`chatCenterPnc flex-1 bg-[#FFFFFF] rounded-lg ${
              showPromptSuggestion ? "chatCenterPnc-with-suggestions" : ""
            }`}
          >
            {/* Chat Messages Area */}
            <div className="h-full overflow-y-auto">
              <div
                className="flex-1 overflow-hidden mainChatPnc"
                ref={chatContainerRef}
              >
                <div className="mx-auto space-y-6 firstChatBot">
                  {/* Render messages */}
                  {messages.map((message, index) => {
                    // Determine message type with specific checks
                    const isUserMessage =
                      message.sender === "user" &&
                      !message.isUserFeedback &&
                      !message.isYesOption;
                    const isUserFeedback = message.isUserFeedback === true;
                    const isYesOption = message.isYesOption === true;
                    const isAssistant =
                      message.sender === "assistant" &&
                      !message.isInitialMessage &&
                      !message.isFollowUp &&
                      !message.isChangeSelectionResponse &&
                      !message.isRephraseResponse;
                    const isInitialMessage = message.isInitialMessage === true;
                    const isFollowUp = message.isFollowUp === true;
                    const isSearchParams = message.type === "searchParameters";
                    const isYesSelection = message.showYesSelection === true;
                    const isChangeSelectionResponse =
                      message.isChangeSelectionResponse === true;
                    const isRephraseResponse =
                      message.isRephraseResponse === true &&
                      !message.showRephraseQuestion;
                    const showRephraseQuestion =
                      message.showRephraseQuestion === true;
                    const isPromptSuggestion =
                      message.type === "promptSuggestion" &&
                      message.showPromptSuggestion;

                    return (
                      <React.Fragment
                        key={message.messageId || `message-${index}`}
                      >
                        {/* Regular user messages */}
                        {isUserMessage && (
                          <UserMessage
                            message={message}
                            cycleId={message.cycleId || conversationCycleId}
                            id={message.messageId}
                          />
                        )}

                        {/* User feedback messages */}
                        {isUserFeedback && (
                          <div className="bg-gray-100 rounded-lg ml-auto max-w-[10%] userPartPnc p-2 custom-tab">
                            <p className="questionPartPnc font-medium">
                              {message.text === "Yes" ? t("yes") : t("no")}
                            </p>
                          </div>
                        )}

                        {/* Yes option selection messages */}
                        {message.isYesOption && (
                          <div className="bg-gray-100 rounded-lg ml-auto userPartPnc mt-[16px] p-2 userSuggest">
                            <p className="questionPartPnc font-medium">
                              {getMessageText(message)}
                            </p>
                          </div>
                        )}

                        {/* YesSelection component */}
                        {isYesSelection && (
                          <div className="mt-4 fusoChatFirst queryMarkPnc">
                            <img
                              src={fusoChat}
                              alt="FUSO"
                              className="w-6 h-6 fusoLogoSmall"
                            />
                            <div className="bg-red-50 rounded-lg p-4 custom-radius ">
                              <style jsx>{`
                                /* Dynamic styling to hide buttons when needed */
                                .hide-buttons-container button {
                                  display: none;
                                }
                              `}</style>
                              <div
                                className={
                                  message.hideButtons
                                    ? "hide-buttons-container"
                                    : ""
                                }
                              >
                                <YesSelection
                                  handleRephraseClick={() =>
                                    handleYesSelectionOption("rephrase")
                                  }
                                  handleChangeSelectionsClick={() =>
                                    handleYesSelectionOption("change")
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Rephrase question component */}
                        {showRephraseQuestion && (
                          <RephraseQuestion message={message} />
                        )}

                        {/* Rephrase question response */}
                        {isRephraseResponse && (
                          <div className="chatsuggPnc">
                            <div className="iconOuter">
                              <img
                                src={fusoChat}
                                alt="FUSO"
                                className="w-6 h-6 fusoLogoSmall"
                              />
                            </div>
                            <div className="bg-red-50 rounded-lg p-4 pncChatFirst questionResponse">
                              <div>
                                <p className="flex gap-[4px] chatSuggestionPnc">
                                  {message.text}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Change selection response */}
                        {message.isChangeSelectionResponse && (
                          <div className="chatsuggPnc">
                            <div className="iconOuter">
                              <img
                                src={fusoChat}
                                alt="FUSO"
                                className="w-6 h-6 fusoLogoSmall"
                              />
                            </div>
                            <div className="bg-red-50 rounded-lg p-4 pncChatFirst selectResponsePnc">
                              <div>
                                <p className="flex gap-[4px] chatSuggestionPnc">
                                  {getMessageText(message)}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Assistant messages */}
                        {isAssistant && (
                          <FusoChatMessage
                            message={message}
                            index={index}
                            handleFeedback={handleFeedback}
                            dots={dots}
                            formatResponse={formatResponse}
                            cycleId={message.cycleId || conversationCycleId}
                            chat_record_id={message.chat_record_id}
                            user_feedback={message.user_feedback}
                            isHistoryView={message.isHistoryView || false}
                            pdf_link={message.pdf_link}
                          />
                        )}

                        {/* Initial messages - THIS IS WHERE THE CHANGE IS MADE */}
                        {isInitialMessage && (
                          <div className="chatsuggPnc">
                            <div className="iconOuter">
                              <img
                                src={fusoChat}
                                alt="FUSO"
                                className="w-6 h-6 fusoLogoSmall"
                              />
                            </div>
                            <div className="bg-red-50 rounded-lg p-4 pncChatFirst responsePnc">
                              <div>
                                <p className="flex gap-[4px] chatSuggestionPnc">
                                  {/* CHANGE: Use translation instead of message.text */}
                                  {message.textKey
                                    ? t(message.textKey)
                                    : t("allSetMessageShop")}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Follow up messages */}
                        {isFollowUp && (
                          <FusoChatEndMessage
                            message={message}
                            askAnotherQueryClicked={askAnotherQueryClicked}
                            handleResetQuery={handleResetQuery}
                          />
                        )}

                        {/* Search parameters */}
                        {isSearchParams && (
                          <SearchParameters
                            key={`${message.cycleId}-search-params`}
                            searchType={searchType}
                            setSearchType={setSearchType}
                            vinInput={vinInput}
                            handleVinInputChange={handleVinInputChange}
                            isLoadingVin={isLoadingVin}
                            vinError={vinError}
                            modelInput={modelInput}
                            classificationInput={classificationInput}
                            handleModelInputChange={handleModelInputChange}
                            handleClassificationInputChange={
                              handleClassificationInputChange
                            }
                            isLoadingModelClass={isLoadingModelClass}
                            modelClassError={modelClassError}
                            isSearchValid={isSearchValid}
                            groups={groups}
                            subGroups={subGroups}
                            selectedGroup={selectedGroup}
                            selectedGroupMainGroup={selectedGroupMainGroup}
                            selectedSubGroup={selectedSubGroup}
                            selectedSubGroupMainGroup={
                              selectedSubGroupMainGroup
                            }
                            groupDropdownOpen={groupDropdownOpen}
                            subGroupDropdownOpen={subGroupDropdownOpen}
                            setGroupDropdownOpen={setGroupDropdownOpen}
                            setSubGroupDropdownOpen={setSubGroupDropdownOpen}
                            handleGroupSelect={handleGroupSelect}
                            handleSubGroupSelect={handleSubGroupSelect}
                            isLoadingSubGroups={isLoadingSubGroups}
                            resetSearchState={resetSearchState}
                            cycleId={message.cycleId || conversationCycleId}
                            onValidationChange={onValidationChange}
                            preserveValues={askAnotherQueryClicked}
                          />
                        )}

                        {/* Show thinking indicator after last message if thinking */}
                        {isThinking && index === messages.length - 1 && (
                          <div className="fusoChatFirst mt-4 queryMarkPnc">
                            <img
                              src={fusoChat}
                              alt="FUSO"
                              className="w-6 h-6 fusoLogoSmall"
                            />
                            <div className="bg-red-50 rounded-lg p-2">
                              <div className="flex gap-3">
                                <div className="flex-1">
                                  <p className="thinkingPnc">
                                    {t("thinking")}
                                    <span className="dots-animation">
                                      {dots}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div
                    ref={messagesEndRef}
                    style={{ height: "1px", margin: "0" }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Chat input area */}
          <div className="items-center focus-within:ring-custom footerBoxPnc">
            {/* Input box with logo */}
            <div className="flex">
              <img
                src={logo}
                alt="icon"
                className="h-6 w-6 ml-2 cursor-pointer"
              />
              {/* Input Field */}
              <textarea
                type="text"
                placeholder={t("askAnything")}
                className="flex-1 p-2 focus:outline-none resize-none textarea-align-top"
                value={userInput}
                onChange={handleUserInputChange}
                onKeyPress={handleKeyPress}
                style={{
                  fontSize: "14px",
                  lineHeight: "1.4",
                  caretColor: "#666",
                }}
              />
            </div>

            {/* Action buttons */}
            <div className="flex justify-end items-center gap-[10px]">
              {/* Speech-to-Text Button */}
              <div
                className={`speech-to-text-btn mr-2 relative flex items-center justify-center ${
                  isListening ? "active" : ""
                } cursor-pointer`}
                onClick={toggleSpeechRecognition}
                title={isListening ? t("stopListening") : t("startVoiceInput")}
              >
                {/* Circular background */}
                <div
                  className={`absolute rounded-full w-10 h-10 ${
                    isListening ? "bg-blue-100" : "bg-gray-100"
                  }`}
                ></div>
                {/* Microphone icon */}
                <img
                  src={isListening ? micActiveIcon : micIcon}
                  alt="Voice Input"
                  className="h-6 w-6 relative z-10"
                />
              </div>

              {/* Send button - with mobile-specific styling */}
              <div className="send-button-container">
                {/* Desktop send button */}
                {isSendButtonDisabled ? (
                  <div className="flex items-center bg-[#EDEDED] text-white px-4 py-2 rounded cursor-not-allowed sendDisabledPnc desktop-send-btn">
                    {t("send")}
                    <img src={sendGray} alt="Send Button" />
                  </div>
                ) : (
                  <div
                    onClick={handleSendMessage}
                    className="sendGrayPnc flex items-center cursor-pointer text-white px-4 py-2 rounded desktop-send-btn"
                  >
                    {t("send")}
                    <img src={sendWhite} alt="Send Button" />
                  </div>
                )}

                {/* Mobile send button */}
                {isSendButtonDisabled ? (
                  <div className="mobile-send-btn bg-[#EDEDED] w-10 h-10 rounded-full flex items-center justify-center cursor-not-allowed">
                    <img src={sendGray} alt="Send Button" className="h-5 w-5" />
                  </div>
                ) : (
                  <div
                    onClick={handleSendMessage}
                    className="mobile-send-btn bg-[#1C1D20] w-10 h-10 rounded-full flex items-center justify-center cursor-pointer"
                  >
                    <img
                      src={sendWhite}
                      alt="Send Button"
                      className="h-5 w-5"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default Main;
